/**
 * DataContext - initializes and provides the database as annoteDB.
 * The database could come from the extension or from Supabase.
 */
import { authLogger, storageLogger } from '@annote/core/utils/debug';
import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { IDatabase } from '@annote/core/dbs/types';
import { AppMessageDatabase } from '@annote/core/dbs/AppMessageDb';
import { SupabaseDatabase, isSupabaseDatabase } from '@annote/core/dbs/supabaseDb';
import { debounce } from 'lodash';

const functionsUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
if (!functionsUrl) throw new Error('DataProvider - setSupabaseAsAnnoteDB - No functions URL found');
let highlightingInitialized = false;

type DataContextType = {
  annoteDB: IDatabase | null;
  isUsingExtension: boolean;
  isInitialized: boolean;
  highlightReaderSources: ((url: string) => void) | null;
};

const DataContext = createContext<DataContextType | null>(null);

// Provided before ProfileProvider.
export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [annoteDB, setAnnoteDB] = useState<IDatabase | null>(null);
  const [isUsingExtension, setUsingExtension] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [highlightReaderSources, setHighlightReaderSources] = useState<((url: string) => void) | null>(null);
  const hasInitialized = useRef(false);

  // if debug mode, set the annoteDB on the window object
  if (process.env.NODE_ENV === 'development') {
    useEffect(() => {
      window.annoteDB = annoteDB;
    }, [annoteDB]);
  }

  const attemptExtensionSetup = async (): Promise<boolean> => {
    const appDB = new AppMessageDatabase();

    const waitForExtension = (): Promise<void> => {
      return new Promise((resolve) => {
        const listener = (event: MessageEvent): void => {
          if (event.source !== window) return;
          if (event.data.messageType === 'APP_LISTENER_READY') {
            window.removeEventListener('message', listener);
            resolve();
          }
        };
        window.addEventListener('message', listener);
      });
    };

    try {
      await Promise.race([
        waitForExtension(),
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Extension timeout')), 2000);
        }),
      ]);

      // Route token requests through the extension so authenticated APIs
      // (e.g. Anni) work when the extension owns the Supabase session.
      const { registerFallbackTokenProvider } = await import('src/helpers/auth');
      registerFallbackTokenProvider(async () => {
        const authToken = await appDB.getAuthToken();
        return authToken.token;
      });

      // Give the app's Supabase client the extension's session so direct
      // supabase reads (Anni's suggestions, auto-suggestions, RLS queries)
      // work in extension mode. IMPORTANT: this client must never refresh the
      // token itself - the extension owns the refresh token, and rotating it
      // from two places would invalidate the extension's session. We stop
      // auto-refresh and instead re-pull a fresh token from the extension
      // every 20 minutes, well inside the 1 hour access-token lifetime.
      const { supabaseClient } = await import('src/db/supabase');
      const syncExtensionSession = async (): Promise<void> => {
        try {
          const authToken = await appDB.getAuthToken();
          if (authToken?.token && authToken?.refreshToken) {
            supabaseClient.auth.stopAutoRefresh();
            await supabaseClient.auth.setSession({
              access_token: authToken.token,
              refresh_token: authToken.refreshToken,
            });
          }
        } catch (sessionError) {
          authLogger.debug('[DataContext] Could not sync extension session into supabase client:', sessionError);
        }
      };
      await syncExtensionSession();
      window.setInterval(syncExtensionSession, 20 * 60 * 1000);

      // Populate the cached profile so auth gates see an existing extension
      // session on fresh page loads - this AppMessageDatabase is new on every
      // load, but the extension may already be signed in.
      try {
        appDB.profile = (await appDB.getProfile()) ?? undefined;
      } catch (profileError) {
        authLogger.debug('[DataContext] Extension present but profile unavailable:', profileError);
      }

      setAnnoteDB(appDB);
      setUsingExtension(true);
      setIsInitialized(true);
      return true;
    } catch (error) {
      return false;
    }
  };

  const initializeDatabase = async (): Promise<void> => {
    authLogger.debug('[DataContext] initializeDatabase() called');

    // makes sure we don't double initialize - strict mode can try to double run this
    if (hasInitialized.current) {
      authLogger.debug('[DataContext] Already initialized, skipping');
      return;
    }
    hasInitialized.current = true;

    try {
      // Try extension first if in Chrome
      const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
      authLogger.debug('[DataContext] Browser check - isChrome:', isChrome, 'userAgent:', navigator.userAgent);

      if (isChrome) {
        authLogger.debug('[DataContext] Attempting extension setup...');
        const extensionSuccess = await attemptExtensionSetup();
        if (extensionSuccess) {
          authLogger.debug('[DataContext] Extension setup successful');
          return;
        }
        authLogger.debug('[DataContext] Extension setup failed, falling back to Supabase');
      }

      // No Extension - Fall back to Supabase - dynamically import the client
      authLogger.debug('[DataContext] Importing Supabase client...');
      const { supabaseClient } = await import('src/db/supabase');
      authLogger.debug('[DataContext] Supabase client imported successfully');

      if (process.env.NODE_ENV === 'development') {
        window.supabase = supabaseClient;
      }

      authLogger.debug('[DataContext] Creating SupabaseDatabase instance...');
      const supabaseDB = new SupabaseDatabase(supabaseClient, functionsUrl);
      authLogger.debug('[DataContext] Calling supabaseDB.init()...');
      await supabaseDB.init();
      authLogger.debug('[DataContext] supabaseDB.init() completed, userId:', supabaseDB.userId || 'none');

      // Listen for auth state changes to handle token refresh
      const { data: authListener } = supabaseClient.auth.onAuthStateChange(async (event, session) => {
        authLogger.debug('Auth state changed:', event, session ? 'has session' : 'no session');

        // Update userId when session changes (refresh, sign in, etc.)
        if (session?.user?.id && event === 'TOKEN_REFRESHED') {
          supabaseDB.userId = session.user.id;
        }

        // If signed out, clear the database
        if (event === 'SIGNED_OUT') {
          supabaseDB.userId = undefined;
          supabaseDB.profile = null;
        }
      });

      // Store the unsubscribe function to clean up later if needed
      (window as Window & { authUnsubscribe?: () => void }).authUnsubscribe = authListener.subscription.unsubscribe;

      authLogger.debug('[DataContext] Setting annoteDB and isInitialized=true');
      setAnnoteDB(supabaseDB);
      setIsInitialized(true);
      setUsingExtension(false);
      authLogger.debug('[DataContext] Initialization complete - success');
    } catch (error) {
      authLogger.error('[DataContext] Initialization error:', error);
      storageLogger.error('DataProvider - initialization error:', error);
      // Don't re-throw - let the app continue without auth.
      // The user will be shown the login page since annoteDB is null.
      // Reset flag so we can try again on next mount (e.g., after login)
      hasInitialized.current = false;
      setIsInitialized(true); // Mark as initialized so app can render
    }
  };

  useEffect(() => {
    initializeDatabase();

    // Cleanup auth listener on unmount
    return () => {
      const unsubscribe = (window as Window & { authUnsubscribe?: () => void }).authUnsubscribe;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    // annoteDB is only set when initialization is complete, so checking it is sufficient
    if (!annoteDB) return;
    if (!isSupabaseDatabase(annoteDB)) return;

    const initHighlighting = async (): Promise<void> => {
      if (highlightingInitialized) return;
      highlightingInitialized = true;

      // import annote-embed as a module/lib for highlighting sources
      // In test environment, vitest.config.ts aliases this to a stub file
      // eslint-disable-next-line import/no-unresolved
      const annoteEmbed = await import('@extension/dist/lib/annote-embed.js');
      await annoteEmbed.default({ annoteDB, functionsUrl });

      // re-highlight a page by url
      const debouncedHighlight = debounce(() => {
        annoteEmbed.default({ annoteDB, functionsUrl });
      }, 300);

      setHighlightReaderSources(() => debouncedHighlight);
    };

    initHighlighting();
  }, [annoteDB]);

  return (
    <DataContext.Provider value={{ annoteDB, isUsingExtension, isInitialized, highlightReaderSources }}>
      {children}
    </DataContext.Provider>
  );
};

export const useDataContext = (): DataContextType => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within a DataProvider');
  }
  return context;
};
